import React from 'react';
import { Button, TextField, Link, Typography, Container, Card, CardContent, InputAdornment, FormControlLabel, Checkbox, Box, Alert } from '@mui/material';
import { EmailOutlined, LockOutlined, ShieldOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { closeAlert } from '../redux/slices/alerts';
import { forgotPassword, setForgotPassword, clearMfa } from '../redux/slices/auth';
import { verify2FA } from '../redux/ducks/twoFactor';
import styled from '@emotion/styled';
import { useBrandName } from '../utils/brandUtils';

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  minHeight: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.up('sm')]: {
    width: '475px',
    margin: '0 auto',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const StyledInlineAlert = styled(Alert)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const TwoFactorHelper = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const StepHelper = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const StyledReasonAlert = styled(Alert)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

const TwoFactorToggleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  width: '100%',
}));

const REASON_TEXT = {
  PASSWORD_NOT_SET: "Your password from the old system doesn't carry over. We'll email you a sign-in code to get started.",
  FIRST_TIME: "No password yet? We'll email you a sign-in code so you can get in and choose one.",
};

const STEP_HELPER = {
  1: "Enter the email address we have on file. We'll send you a 6-digit code to sign in with.",
  2: 'Check your email for the 6-digit code and enter it below. The code expires in 10 minutes.',
};

function ForgotPassword() {
  const dispatch = useDispatch();
  const brandName = useBrandName();
  const prefilledEmail = useSelector(state => state.auth.forgotPasswordEmail);
  const reason = useSelector(state => state.auth.forgotPasswordReason);
  const [formData, setFormData] = React.useState(prefilledEmail ? { email: prefilledEmail } : {});
  const forgotPasswordStep = useSelector(state => state.auth.forgotPassword);
  const mfaToken = useSelector(state => state.auth.mfaToken);
  const mfaError = useSelector(state => state.auth.mfaError);
  const [twoFactorCode, setTwoFactorCode] = React.useState('');
  const [useBackupCode, setUseBackupCode] = React.useState(false);
  const [rememberDevice, setRememberDevice] = React.useState(false);

  React.useEffect(() => {
    if (forgotPasswordStep === 1) {
      setFormData((prev) => ({ ...prev, otp: '' }));
    }
  }, [forgotPasswordStep]);

  const handleFormChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [event.target.name]: value,
    });
  };

  const endForgotPassword = (event) => {
    dispatch(clearMfa());
    dispatch(setForgotPassword({ step: 0 }));
  };

  const handleForgottenPassword = (event) => {
    event.preventDefault();
    dispatch(closeAlert());
    dispatch(forgotPassword({ email: formData.email, otp: formData.otp }));
  };

  const handleVerify2FA = (event) => {
    event.preventDefault();
    if (!twoFactorCode.trim()) return;
    dispatch(verify2FA({ mfaToken, code: twoFactorCode.trim(), rememberDevice }));
  };

  return (
    <StyledContainer component="main">
      <StyledCard>
        <StyledCardContent component="form">
          <Typography variant="h4" component="h1" gutterBottom>
            {forgotPasswordStep === 3 ? 'Two-Factor Authentication' : 'Sign in with an email code'}
          </Typography>
          {forgotPasswordStep !== 3 && REASON_TEXT[reason] && (
            <StyledReasonAlert severity="info">{REASON_TEXT[reason]}</StyledReasonAlert>
          )}
          {STEP_HELPER[forgotPasswordStep] && (
            <StepHelper variant="body2">{STEP_HELPER[forgotPasswordStep]}</StepHelper>
          )}
          {forgotPasswordStep === 1 &&
            <StyledTextField
              required
              fullWidth
              id="email"
              variant="outlined"
              name="email"
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              value={formData.email || ''}
              onChange={handleFormChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EmailOutlined />
                  </InputAdornment>
                ),
              }}
            />
          }
          {forgotPasswordStep === 2 &&
            <StyledTextField
              required
              fullWidth
              autoFocus
              id="otp"
              variant="outlined"
              name="otp"
              label="Sign-in code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={formData.otp || ''}
              onChange={handleFormChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LockOutlined />
                  </InputAdornment>
                ),
              }}
            />
          }
          {forgotPasswordStep === 3 && (
            <>
              <TwoFactorHelper variant="body2">
                {useBackupCode
                  ? 'Enter one of your backup codes to continue.'
                  : 'Enter the 6-digit code from your authenticator app.'}
              </TwoFactorHelper>
              <StyledTextField
                required
                fullWidth
                autoFocus
                id="twoFactorCode"
                variant="outlined"
                name="twoFactorCode"
                label={useBackupCode ? 'Backup Code' : 'Authentication Code'}
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <ShieldOutlined />
                    </InputAdornment>
                  ),
                }}
              />
              <TwoFactorToggleRow>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                    />
                  }
                  label="Remember this device for 30 days"
                />
                <StyledLink variant="body2" onClick={() => { setUseBackupCode((v) => !v); setTwoFactorCode(''); }}>
                  {useBackupCode ? 'Use authenticator app code instead' : 'Use a backup code instead'}
                </StyledLink>
              </TwoFactorToggleRow>
              {mfaError && (
                <StyledInlineAlert severity="error">
                  {useBackupCode && /Invalid authentication code/i.test(mfaError) ? 'Invalid backup code' : mfaError}
                </StyledInlineAlert>
              )}
            </>
          )}
          <StyledButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            onClick={forgotPasswordStep === 3 ? handleVerify2FA : handleForgottenPassword}
          >
            {forgotPasswordStep === 3 ? 'Verify' : forgotPasswordStep === 2 ? 'Sign in' : 'Email me a code'}
          </StyledButton>
          <Typography variant="body2" align="center">
            Already have a password?{' '}
            <StyledLink onClick={endForgotPassword}>
              Sign in with your password
            </StyledLink>
          </Typography>
        </StyledCardContent>
      </StyledCard>

      <StyledCopyright variant="body2" color="textSecondary" align="center">
        {'Copyright © '}
        <Link color="inherit" href="https://pollux-mui.com/">
          {brandName}
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </StyledCopyright>
    </StyledContainer>
  );
}

export default ForgotPassword;
